import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { Plus, PencilLine, CheckCircle, Trash, Warning, Package } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { MatchAssign, StockItemForm, BASE_UNITS } from "../components/MatchAssign";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_ITEM = { name: "", base_unit: "unit", category_id: "" };
const TYPE_LABELS = { supplier_invoice: "Supplier Invoice", receipt: "Receipt", credit_note: "Credit Note" };

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function ReviewImage({ invId, idx }) {
  const [failed, setFailed] = useState(false);
  const src = `${API_BASE}/invoices/${invId}/image/${idx}`;
  if (failed) {
    return (
      <a href={src} target="_blank" rel="noreferrer" data-testid={`review-image-fallback-${idx}`}
        className="block w-full rounded-lg border border-[#B33939]/40 bg-[#B33939]/5 p-6 text-center text-sm text-muted-foreground">
        Photo failed to load here — tap to open it directly
      </a>
    );
  }
  return <img src={src} alt={`Invoice page ${idx + 1}`} data-testid={`review-image-${idx}`}
    className="w-full rounded-lg border border-border glass" loading="lazy" onError={() => setFailed(true)} />;
}

export function InvoiceReviewPanel() {
  const [queue, setQueue] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const load = useCallback(() => {
    api.get("/invoices/review/queue").then((r) => setQueue(r.data)).catch(() => toast.error("Failed to load review queue"));
    api.get("/stock-items").then((r) => setStockItems(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/invoice-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return <ReviewQueue queue={queue} stockItems={stockItems} setStockItems={setStockItems} suppliers={suppliers} categories={categories} onDone={load} />;
}

function ReviewQueue({ queue, stockItems, setStockItems, suppliers, categories, onDone }) {
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newItemFor, setNewItemFor] = useState(null);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const [quickFor, setQuickFor] = useState(null);
  const [quick, setQuick] = useState(null);

  const pick = (inv) => { setSel(inv); setDraft(JSON.parse(JSON.stringify(inv))); };
  const setLine = (i, patch) => setDraft((d) => ({ ...d, lines: undefined, line_items: d.line_items.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));

  const openQuick = (i) => {
    const l = draft.line_items[i];
    const qty = Number(l.quantity) || 0;
    const tot = Number(l.line_total) || 0;
    setQuick({
      name: (l.raw_description || "").trim(),
      base_unit: "unit",
      category_id: draft.category_id || "",
      cost: qty > 0 && tot > 0 ? (tot / qty).toFixed(2) : "",
    });
    setQuickFor(i);
  };

  const createQuick = async () => {
    if (!quick.name.trim()) return toast.error("Item name required");
    try {
      const { data } = await api.post("/stock-items", {
        name: quick.name, base_unit: quick.base_unit, category_id: quick.category_id || null,
        initial_cost: quick.cost === "" ? null : Number(quick.cost),
      });
      setStockItems((s) => [...s, data].sort((a, b) => a.name.localeCompare(b.name)));
      setLine(quickFor, { stock_item_id: data.id, stock_item_name: data.name });
      setQuickFor(null); setQuick(null);
      toast.success(`'${data.name}' quick-added and matched`);
    } catch (e) { toast.error(apiError(e, "Quick add failed")); }
  };

  const createItem = async () => {
    if (!newItem.name.trim()) return toast.error("Item name required");
    try {
      const { data } = await api.post("/stock-items", { ...newItem, category_id: newItem.category_id || null });
      setStockItems((s) => [...s, data].sort((a, b) => a.name.localeCompare(b.name)));
      setLine(newItemFor, { stock_item_id: data.id, stock_item_name: data.name });
      setNewItemFor(null); setNewItem(EMPTY_ITEM);
      toast.success(`Stock item '${data.name}' created`);
    } catch (e) { toast.error(apiError(e, "Create failed")); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const payload = {
        date: draft.date, type: draft.type, supplier_id: draft.supplier_id, category_id: draft.category_id,
        amount: num(draft.amount) ?? 0, invoice_number: draft.invoice_number || null,
        subtotal: num(draft.subtotal), vat: num(draft.vat),
        lines: draft.line_items.map((l) => ({
          id: l.id, raw_description: l.raw_description, quantity: num(l.quantity),
          unit_of_measure: l.unit_of_measure || null, unit_price: num(l.unit_price), line_total: num(l.line_total),
          stock_item_id: l.stock_item_id || null, purchase_unit: l.purchase_unit || null,
          pack_size: num(l.pack_size), pack_unit: l.pack_unit || null,
        })),
      };
      await api.post(`/invoices/${draft.id}/confirm`, payload);
      toast.success("Invoice confirmed and posted to inventory");
      setSel(null); setDraft(null); onDone();
    } catch (e) { toast.error(apiError(e, "Confirm failed")); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    try {
      await api.delete(`/invoices/${draft.id}`);
      toast.success("Invoice discarded");
      setSel(null); setDraft(null); onDone();
    } catch (e) { toast.error(apiError(e, "Discard failed")); }
  };

  if (!sel || !draft) {
    return (
      <div className="space-y-3" data-testid="review-queue-list">
        {queue.length === 0 && <p className="text-muted-foreground py-8 text-center">Nothing waiting for review.</p>}
        {queue.map((inv) => (
          <Card key={inv.id} className="border-border shadow-none cursor-pointer hover:border-primary/50 transition-colors" data-testid={`review-item-${inv.id}`} onClick={() => pick(inv)}>
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">{inv.supplier_name} · {zar(inv.amount)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{inv.date} · {TYPE_LABELS[inv.type]} · {(inv.line_items || []).length} lines · confidence {Math.round((inv.extraction_confidence || 0) * 100)}%</p>
              </div>
              <div className="flex flex-wrap gap-1 justify-end">
                {(inv.flags || []).map((f) => <Badge key={f} variant="outline" className="text-[#F87171] border-[#B33939]/40"><Warning size={12} className="mr-1" />{f.replace(/_/g, " ")}</Badge>)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="review-detail">
      <div className="space-y-3">
        <Button variant="ghost" size="sm" data-testid="review-back-btn" onClick={() => { setSel(null); setDraft(null); }}>← Back to queue</Button>
        {(draft.image_paths || []).map((p, i) => <ReviewImage key={p} invId={draft.id} idx={i} />)}
        {!(draft.image_paths || []).length && (
          <p className="text-sm text-muted-foreground border border-border rounded-lg p-6 text-center" data-testid="review-no-image">
            No photo stored for this invoice.
          </p>
        )}
      </div>
      <div className="space-y-4">
        {(draft.flags || []).length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="review-flags">
            {draft.flags.map((f) => <Badge key={f} variant="outline" className="text-[#F87171] border-[#B33939]/40"><Warning size={12} className="mr-1" />{f.replace(/_/g, " ")}</Badge>)}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Supplier {(draft.flags || []).includes("supplier_unmatched") && <span className="text-[#F87171]">· pick match</span>}</Label>
            <Select value={draft.supplier_id || ""} onValueChange={(v) => setDraft({ ...draft, supplier_id: v })}>
              <SelectTrigger data-testid="review-supplier" className={(draft.flags || []).includes("supplier_unmatched") ? "border-[#B33939]" : ""}><SelectValue placeholder="Pick supplier…" /></SelectTrigger>
              <SelectContent>
                {(draft.supplier_candidates || []).map((c) => <SelectItem key={c.supplier_id} value={c.supplier_id}>{c.name} (suggested)</SelectItem>)}
                {suppliers.filter((s) => !(draft.supplier_candidates || []).some((c) => c.supplier_id === s.id)).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Category {(draft.flags || []).includes("category_unmatched") && <span className="text-[#F87171]">· pick match</span>}</Label>
            <Select value={draft.category_id || ""} onValueChange={(v) => setDraft({ ...draft, category_id: v })}>
              <SelectTrigger data-testid="review-category" className={(draft.flags || []).includes("category_unmatched") ? "border-[#B33939]" : ""}><SelectValue placeholder="Pick category…" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.department}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Date</Label><Input type="date" data-testid="review-date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className={(draft.flags || []).includes("date_in_future") || (draft.flags || []).includes("date_unparseable") ? "border-[#B33939]" : ""} /></div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
              <SelectTrigger data-testid="review-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Invoice #</Label><Input data-testid="review-invoice-number" value={draft.invoice_number || ""} onChange={(e) => setDraft({ ...draft, invoice_number: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Total (R) {(draft.flags || []).includes("subtotal_vat_vs_total_mismatch") && <span className="text-[#F87171]">· check</span>}</Label><Input type="number" step="any" data-testid="review-total" value={draft.amount ?? ""} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className={(draft.flags || []).includes("subtotal_vat_vs_total_mismatch") ? "border-[#B33939]" : ""} /></div>
          <div className="space-y-1.5"><Label>Subtotal (R)</Label><Input type="number" step="any" data-testid="review-subtotal" value={draft.subtotal ?? ""} onChange={(e) => setDraft({ ...draft, subtotal: e.target.value })} className={(draft.flags || []).includes("lines_vs_subtotal_mismatch") ? "border-[#B33939]" : ""} /></div>
          <div className="space-y-1.5"><Label>VAT (R)</Label><Input type="number" step="any" data-testid="review-vat" value={draft.vat ?? ""} onChange={(e) => setDraft({ ...draft, vat: e.target.value })} /></div>
        </div>

        <div className="space-y-3" data-testid="review-lines">
          <p className="font-heading font-semibold text-sm">Line items</p>
          {draft.line_items.map((l, i) => (
            <div key={l.id} className={`border rounded-lg p-3 space-y-2 ${(l.flags || []).length ? "border-[#B33939]/50 bg-[#B33939]/5" : "border-border"}`} data-testid={`review-line-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <Input value={l.raw_description} onChange={(e) => setLine(i, { raw_description: e.target.value })} data-testid={`review-line-desc-${i}`} className="font-medium" />
                <span className={`text-xs whitespace-nowrap ${l.confidence_score >= 0.85 ? "text-[#A9C97E]" : "text-[#F87171]"}`}>{Math.round((l.confidence_score || 0) * 100)}%</span>
              </div>
              {(l.flags || []).length > 0 && <div className="flex flex-wrap gap-1">{l.flags.map((f) => <Badge key={f} variant="outline" className="text-[#F87171] border-[#B33939]/40 text-[10px]">{f.replace(/_/g, " ")}</Badge>)}</div>}
              {l.price_drift_pct != null && (
                <p className="text-xs text-[#F4A261]" data-testid={`review-line-drift-${i}`}>
                  Billed {l.price_drift_pct}% above catalog list price{l.catalog_list_price != null ? ` (${zar(l.catalog_list_price)})` : ""} — check before posting
                </p>
              )}
              <div className="grid grid-cols-4 gap-2">
                <Input type="number" step="any" placeholder="Qty" value={l.quantity ?? ""} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`review-line-qty-${i}`} />
                <Input placeholder="Unit" value={l.unit_of_measure || ""} onChange={(e) => setLine(i, { unit_of_measure: e.target.value })} data-testid={`review-line-uom-${i}`} />
                <Input type="number" step="any" placeholder="Unit price" value={l.unit_price ?? ""} onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`review-line-price-${i}`} />
                <Input type="number" step="any" placeholder="Total" value={l.line_total ?? ""} onChange={(e) => setLine(i, { line_total: e.target.value })} data-testid={`review-line-total-${i}`} />
              </div>
              <MatchAssign line={l} index={i} stockItems={stockItems} idPrefix="review-line"
                onPatch={setLine} onQuick={openQuick} onNew={setNewItemFor} />
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <Button onClick={confirm} disabled={busy} data-testid="review-confirm-btn" className="flex-1 bg-primary hover:bg-[#8B5CF6] text-white">
            <CheckCircle size={16} className="mr-1" /> {busy ? "Posting…" : "Confirm & post to inventory"}
          </Button>
          <Button variant="outline" onClick={discard} data-testid="review-discard-btn" className="text-destructive"><Trash size={15} /></Button>
        </div>
      </div>

      <Dialog open={newItemFor !== null} onOpenChange={(o) => { if (!o) { setNewItemFor(null); setNewItem(EMPTY_ITEM); } }}>
        <DialogContent className="max-w-sm" data-testid="review-new-item-dialog">
          <DialogHeader><DialogTitle className="font-heading">New stock item</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create a stock item for this line</DialogDescription>
          <StockItemForm value={newItem} onChange={setNewItem} categories={categories} />
          <Button onClick={createItem} data-testid="review-new-item-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white mt-3">Create &amp; match</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={quickFor !== null} onOpenChange={(o) => { if (!o) { setQuickFor(null); setQuick(null); } }}>
        <DialogContent className="max-w-sm" data-testid="quick-add-dialog">
          <DialogHeader><DialogTitle className="font-heading">Quick add stock item</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Minimal stock item creation matched to this invoice line</DialogDescription>
          {quick && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="quick-item-name" value={quick.name} onChange={(e) => setQuick({ ...quick, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Unit</Label>
                  <Select value={quick.base_unit} onValueChange={(v) => setQuick({ ...quick, base_unit: v })}>
                    <SelectTrigger data-testid="quick-item-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{BASE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Cost / unit (R)</Label>
                  <Input type="number" step="any" min="0" data-testid="quick-item-cost" value={quick.cost} onChange={(e) => setQuick({ ...quick, cost: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={quick.category_id || "none"} onValueChange={(v) => setQuick({ ...quick, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="quick-item-category"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.department}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createQuick} data-testid="quick-item-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">Quick add &amp; match</Button>
              <p className="text-[11px] text-muted-foreground">Need pack sizes, pars or more detail? Use “Create with full form…” in the match dropdown instead.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Stock() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "review";
  const [queue, setQueue] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [histItem, setHistItem] = useState("");
  const [spendRange, setSpendRange] = useState([format(startOfMonth(new Date()), "yyyy-MM-dd"), format(endOfMonth(new Date()), "yyyy-MM-dd")]);
  const [spend, setSpend] = useState({ by_supplier: [], by_category: [] });
  const [holding, setHolding] = useState(null);
  const [itemOpen, setItemOpen] = useState(false);
  const [itemEdit, setItemEdit] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);

  const loadQueue = useCallback(() => api.get("/invoices/review/queue").then((r) => setQueue(r.data)).catch(() => {}), []);
  const loadItems = useCallback(() => api.get("/stock-items").then((r) => setStockItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadQueue(); loadItems();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/invoice-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [loadQueue, loadItems]);
  useEffect(() => {
    if (tab === "history" && histItem) api.get(`/stock/movements?item_id=${histItem}`).then((r) => setMovements(r.data)).catch(() => {});
  }, [tab, histItem]);
  useEffect(() => {
    if (tab === "spend") api.get(`/stock/spend?start=${spendRange[0]}&end=${spendRange[1]}`).then((r) => setSpend(r.data)).catch(() => {});
    if (tab === "holding") api.get("/stock/holding").then((r) => setHolding(r.data)).catch(() => toast.error("Failed to load stock holding"));
  }, [tab, spendRange]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const totalValue = stockItems.reduce((a, i) => a + (i.stock_value || 0), 0);

  const saveItem = async () => {
    try {
      const payload = { ...itemForm, category_id: itemForm.category_id || null };
      if (itemEdit) { await api.put(`/stock-items/${itemEdit.id}`, payload); toast.success("Item updated"); }
      else { await api.post("/stock-items", payload); toast.success("Item added"); }
      setItemOpen(false); loadItems();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
  };

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Stock &amp; Inventory</h1>
        <p className="text-muted-foreground mt-1">Invoice review queue, stock catalog, price history and spend</p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="review" data-testid="tab-review">Review queue {queue.length > 0 && <Badge className="ml-1.5 bg-[#B33939] text-white text-[10px] px-1.5" data-testid="review-count-badge">{queue.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Stock items</TabsTrigger>
          <TabsTrigger value="holding" data-testid="tab-holding">Stock holding</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Price history</TabsTrigger>
          <TabsTrigger value="spend" data-testid="tab-spend">Spend</TabsTrigger>
        </TabsList>

        <TabsContent value="holding" className="mt-4 space-y-4">
          {!holding ? <p className="text-muted-foreground">Loading…</p> : (
            <>
              <p className="text-sm text-muted-foreground" data-testid="holding-total">
                Total inventory value: <span className="font-semibold text-foreground text-base num">{zar(holding.total_value)}</span>
              </p>
              {holding.groups.map((g) => (
                <div key={g.category} className="glass border border-border rounded-lg overflow-hidden" data-testid={`holding-group-${g.category}`}>
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <p className="font-heading font-semibold">{g.category}{g.department && <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground font-normal">{g.department}</span>}</p>
                    <p className="text-sm num font-semibold">{zar(g.value)}</p>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {g.items.map((i) => (
                        <tr key={i.id} className="border-b last:border-0" data-testid={`holding-item-${i.id}`}>
                          <td className="px-4 py-2">{i.name}</td>
                          <td className="px-4 py-2 num text-right">{i.qty_on_hand} {i.base_unit}</td>
                          <td className="px-4 py-2 num text-right text-muted-foreground">@ {zar(i.current_average_cost)}</td>
                          <td className="px-4 py-2 num text-right font-medium">{zar(i.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {holding.groups.length === 0 && <p className="text-sm text-muted-foreground">No stock items yet.</p>}
            </>
          )}
        </TabsContent>

        <TabsContent value="review" className="mt-4">
          <ReviewQueue queue={queue} stockItems={stockItems} setStockItems={setStockItems} suppliers={suppliers} categories={categories}
            onDone={() => { loadQueue(); loadItems(); }} />
        </TabsContent>

        <TabsContent value="items" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground" data-testid="stock-total-value">Total stock value: <span className="font-semibold text-foreground">{zar(totalValue)}</span></p>
            <Button data-testid="add-stock-item-btn" onClick={() => { setItemEdit(null); setItemForm(EMPTY_ITEM); setItemOpen(true); }} className="bg-primary hover:bg-[#8B5CF6] text-white"><Plus size={16} className="mr-1" /> Add item</Button>
          </div>
          <div className="glass border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="stock-items-table">
              <thead><tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Base unit</th><th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">On hand</th><th className="px-4 py-3 font-medium text-right">Avg cost</th>
                <th className="px-4 py-3 font-medium text-right">Last price</th><th className="px-4 py-3 font-medium text-right">Value</th><th className="px-4 py-3 font-medium"></th>
              </tr></thead>
              <tbody>
                {stockItems.map((i) => (
                  <tr key={i.id} className="border-b last:border-0" data-testid={`stock-item-row-${i.id}`}>
                    <td className="px-4 py-2.5 font-medium">{i.name}</td>
                    <td className="px-4 py-2.5">{i.base_unit}</td>
                    <td className="px-4 py-2.5">{catName(i.category_id)}</td>
                    <td className="px-4 py-2.5 num text-right">{i.qty_on_hand ?? 0}</td>
                    <td className="px-4 py-2.5 num text-right">{zar(i.current_average_cost || 0)}</td>
                    <td className="px-4 py-2.5 num text-right">{i.last_purchase_price != null ? zar(i.last_purchase_price) : "—"}</td>
                    <td className="px-4 py-2.5 num text-right">{zar(i.stock_value || 0)}</td>
                    <td className="px-4 py-2.5"><Button size="sm" variant="ghost" data-testid={`stock-item-edit-${i.id}`} onClick={() => { setItemEdit(i); setItemForm({ name: i.name, base_unit: i.base_unit, category_id: i.category_id || "" }); setItemOpen(true); }}><PencilLine size={15} /></Button></td>
                  </tr>
                ))}
                {stockItems.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No stock items yet — they're also created automatically from the review queue.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          <div className="max-w-sm space-y-1.5">
            <Label>Stock item</Label>
            <Select value={histItem} onValueChange={setHistItem}>
              <SelectTrigger data-testid="history-item"><SelectValue placeholder="Pick item…" /></SelectTrigger>
              <SelectContent>{stockItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {histItem && (
            <div className="glass border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="price-history-table">
                <thead><tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Supplier</th>
                  <th className="px-4 py-3 font-medium text-right">Qty (base)</th><th className="px-4 py-3 font-medium text-right">Unit cost</th><th className="px-4 py-3 font-medium text-right">Total</th>
                </tr></thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} className="border-b last:border-0" data-testid={`movement-row-${m.id}`}>
                      <td className="px-4 py-2.5">{m.date}</td><td className="px-4 py-2.5">{m.supplier_name}</td>
                      <td className="px-4 py-2.5 num text-right">{m.quantity_in_base_units}</td>
                      <td className="px-4 py-2.5 num text-right">{zar(m.unit_cost)}</td>
                      <td className={`px-4 py-2.5 num text-right ${m.total_cost < 0 ? "text-[#F87171]" : ""}`}>{zar(m.total_cost)}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No purchases recorded for this item yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="spend" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" data-testid="spend-start" value={spendRange[0]} onChange={(e) => setSpendRange([e.target.value, spendRange[1]])} /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" data-testid="spend-end" value={spendRange[1]} onChange={(e) => setSpendRange([spendRange[0], e.target.value])} /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[["By supplier", spend.by_supplier, "spend-supplier"], ["By category", spend.by_category, "spend-category"]].map(([title, rows, tid]) => (
              <div key={tid} className="glass border border-border rounded-lg overflow-hidden">
                <p className="px-4 py-3 font-heading font-semibold border-b">{title}</p>
                <table className="w-full text-sm" data-testid={`${tid}-table`}>
                  <tbody>
                    {rows.map((r) => <tr key={r.name} className="border-b last:border-0"><td className="px-4 py-2.5">{r.name}</td><td className="px-4 py-2.5 num text-right">{zar(r.total)}</td></tr>)}
                    {rows.length === 0 && <tr><td className="px-4 py-6 text-center text-muted-foreground">No spend in this range.</td></tr>}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="max-w-sm" data-testid="stock-item-dialog">
          <DialogHeader><DialogTitle className="font-heading">{itemEdit ? "Edit stock item" : "Add stock item"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Stock item details</DialogDescription>
          <StockItemForm value={itemForm} onChange={setItemForm} categories={categories} />
          <Button onClick={saveItem} data-testid="stock-item-save-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white mt-3">{itemEdit ? "Save changes" : "Add item"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
